import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import {
  TextInput,
  DisabledInput,
  SimpleShowLayout,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  BooleanInput,
} from 'react-admin';
import { InputLabel, FormControl } from '@material-ui/core';
import Header from '../../../shared/header';
import {
  email,
  required,
  maxLength,
  minLength,
  validVat,
  stringValue,
  validSiretSiren,
  validElectronicAddress,
  validVatVerified,
} from '../../../core/validators';
import {
  countryList,
  formatSiretSiren,
  getBusinessCountryCode,
  isSiretCountry,
  isSiretRequired,
  SIRET_LENGTH,
} from '../../../shared/country';
import { getBusinessConf } from '../../../core/config';
import { sanitizeVat, getVatFormatExample, WORLDWIDE_VAT_MAX_LENGTH } from '../../../helpers/vatValidation';
import useElectronicAddressSync from '../../../hooks/useElectronicAddressSync';
import useSiretCaret from '../../../hooks/useSiretCaret';
import { PURCHASE_ORDER_REFERENCE_MAX_LENGTH } from '../constants';

const businessCreateFormSelector = formValueSelector('business-create');

const BusinessDetailsUnconnected = ({
  resource,
  classes,
  translate,
  dispatch,
  addressCountryCode,
  billingAddressCountryCode,
  useAsBillingAddress,
  siretSiren,
  electronicAddress,
  purchaseOrderReference,
}) => {
  // Every business identity field follows the billing country, with the main address as fallback,
  // as the back end does.
  const businessCountryCode = getBusinessCountryCode(
    { countryCode: addressCountryCode },
    { countryCode: billingAddressCountryCode },
    useAsBillingAddress
  );
  const showSiret = isSiretCountry(businessCountryCode);

  useElectronicAddressSync({
    formName: 'business-create',
    dispatch,
    businessCountryCode,
    siretSiren,
    electronicAddress,
  });

  const { inputRef: siretInputRef, captureCaret: captureSiretCaret } = useSiretCaret(siretSiren);

  const { config } = getBusinessConf();
  const vatVerifiedExample = getVatFormatExample(businessCountryCode);
  const languageList =
    config && config.translation && config.translation.invoiceLanguage
      ? config.translation.invoiceLanguage.map(language => ({
          id: language.code,
          name: language.label,
        }))
      : [];
  const taxNumberCountry = config && config.taxNumberEnable && (
    <AutocompleteInput
      source="taxNumberCountry"
      choices={countryList}
      className={classes.field}
      classes={{ suggestionsContainerOpen: classes.autoCompleteContainer }}
      autoComplete="nope"
      id="Selenium-Business-Create-Address-Country-Input"
    />
  );
  const communityTaxNumber = config && config.taxNumberEnable && (
    <TextInput
      source="communityTaxNumber"
      validate={[maxLength(20)]}
      className={classes.field}
      id="Selenium-Business-Create-communityTaxNumber-Input"
    />
  );
  const invoiceFrequencyList = [
    {
      id: 'DAILY',
      name: translate(`resources.business.fields.invoiceFrequencies.daily`),
    },
    {
      id: 'MONTHLY',
      name: translate(`resources.business.fields.invoiceFrequencies.monthly`),
    },
  ];

  return (
    <SimpleShowLayout resource={resource}>
      <Header label="resources.business.businessDetails" />
      <TextInput
        source="name"
        required
        validate={[required, stringValue, maxLength(64)]}
        className={classes.field}
        id="Selenium-Business-Create-Name-Input"
      />
      <TextInput
        source="legalName"
        required
        validate={[required, stringValue, maxLength(64)]}
        className={classes.field}
        id="Selenium-Business-Create-legalName-Input"
      />
      <TextInput
        source="phoneNumber"
        className={classes.field}
        validate={[maxLength(15)]}
        id="Selenium-Business-Create-phoneName-Input"
      />
      <TextInput
        source="faxNumber"
        className={classes.field}
        validate={[maxLength(15)]}
        id="Selenium-Business-Create-faxNumber-Input"
      />
      <TextInput
        source="email"
        type="email"
        validate={[email, maxLength(124)]}
        className={classes.field}
        id="Selenium-Business-Create-Email-Input"
      />
      <DisabledInput source="status" className={classes.field} id="Selenium-Business-Create-Status-Input" />
      <TextInput
        source="vat"
        className={classes.field}
        required
        validate={
          config && config.isCountryCodeVat
            ? [required, minLength(4), maxLength(15), validVat]
            : [required, maxLength(32)]
        }
        id="Selenium-Business-Create-Vat-Input"
      />
      {/* Verified VAT (Factur-X BT-48): optional, structurally validated against the business country. */}
      <TextInput
        source="vatVerified"
        label="resources.business.fields.vatVerified"
        className={classes.field}
        validate={[validVatVerified, maxLength(WORLDWIDE_VAT_MAX_LENGTH)]}
        parse={sanitizeVat}
        helperText={
          vatVerifiedExample
            ? translate('resources.business.fields.vatVerifiedHint', { example: vatVerifiedExample })
            : undefined
        }
        options={{ inputProps: { maxLength: WORLDWIDE_VAT_MAX_LENGTH } }}
        id="Selenium-Business-Create-vatVerified-Input"
      />
      {/* EIN is hidden when the SIRET field is displayed to avoid duplicate business info. */}
      {!showSiret && (
        <TextInput
          source="ein"
          validate={[required, maxLength(32)]}
          required
          className={classes.field}
          id="Selenium-Business-Create-ein-Input"
        />
      )}
      <TextInput
        source="sdicemCode"
        label="SDI / PEC"
        className={classes.field}
        validate={[maxLength(124)]}
        id="Selenium-Business-Create-sdicemCode-Input"
      />
      {showSiret && (
        <TextInput
          source="siretSiren"
          label="resources.business.fields.siretSiren"
          className={classes.field}
          validate={[validSiretSiren, maxLength(SIRET_LENGTH + 1)]}
          required={isSiretRequired(businessCountryCode)}
          format={value => formatSiretSiren(value)}
          parse={value => {
            captureSiretCaret(value);
            return value ? value.replace(/\D/g, '') : value;
          }}
          options={{ inputRef: siretInputRef, inputProps: { maxLength: SIRET_LENGTH + 1 } }}
          id="Selenium-Business-Create-siretSiren-Input"
        />
      )}
      {/* Electronic address (BT-49): shown only once a SIRET is present in a SIRET country. */}
      {showSiret && Boolean(siretSiren) && (
        <TextInput
          source="electronicAddress"
          label="resources.business.fields.electronicAddress"
          helperText={translate('resources.business.fields.electronicAddressHint')}
          className={classes.field}
          validate={[validElectronicAddress, maxLength(40)]}
          required={isSiretRequired(businessCountryCode)}
          options={{ inputProps: { maxLength: 40 } }}
          id="Selenium-Business-Create-electronicAddress-Input"
        />
      )}
      <TextInput
        source="purchaseOrderReference"
        className={classes.field}
        validate={[maxLength(PURCHASE_ORDER_REFERENCE_MAX_LENGTH)]}
        helperText={`${(purchaseOrderReference || '').length}/${PURCHASE_ORDER_REFERENCE_MAX_LENGTH}`}
        options={{ inputProps: { maxLength: PURCHASE_ORDER_REFERENCE_MAX_LENGTH } }}
        id="Selenium-Business-Create-purchaseOrderReference-Input"
      />
      <ReferenceInput
        label="resources.business.fields.billingGroup.name"
        source="billingGroup.id"
        resource="name"
        reference="billingGroup"
        className={classes.field}
      >
        <SelectInput source="name" id="Selenium-Business-Create-billingGroup-Select" />
      </ReferenceInput>
      {taxNumberCountry}
      {communityTaxNumber}
      <SelectInput
        source="locale"
        choices={languageList}
        className={classes.field}
        validate={[required, maxLength(5)]}
        required
        id="Selenium-Business-Create-language-Select"
      />
      <SelectInput
        source="invoiceFrequency"
        id="Selenium-Business-Create-invoiceFrequency-Select"
        className={classes.field}
        choices={invoiceFrequencyList}
        validate={[required]}
        required
        allowEmpty={false}
      />
      <FormControl className={classes.field}>
        <InputLabel shrink id="Selenium-Business-Create-MandatoryTripNotes-Label">
          {translate('resources.business.fields.tripNotes')}
        </InputLabel>
        <BooleanInput
          source="mandatoryTripNotes"
          label="resources.business.fields.mandatory"
          id="Selenium-Business-Create-MandatoryTripNotes-Input"
          style={{ marginTop: '12px' }}
        />
      </FormControl>
    </SimpleShowLayout>
  );
};

BusinessDetailsUnconnected.propTypes = {
  classes: PropTypes.shape({}),
  resource: PropTypes.string,
  translate: PropTypes.func.isRequired,
  dispatch: PropTypes.func,
  addressCountryCode: PropTypes.string,
  billingAddressCountryCode: PropTypes.string,
  useAsBillingAddress: PropTypes.bool,
  siretSiren: PropTypes.string,
  electronicAddress: PropTypes.string,
  purchaseOrderReference: PropTypes.string,
};

const mapStateToProps = state => ({
  addressCountryCode: businessCreateFormSelector(state, 'address.countryCode'),
  billingAddressCountryCode: businessCreateFormSelector(state, 'billingAddress.countryCode'),
  useAsBillingAddress: businessCreateFormSelector(state, 'useAsBillingAddress'),
  siretSiren: businessCreateFormSelector(state, 'siretSiren'),
  electronicAddress: businessCreateFormSelector(state, 'electronicAddress'),
  purchaseOrderReference: businessCreateFormSelector(state, 'purchaseOrderReference'),
});

export const BusinessDetails = connect(mapStateToProps)(BusinessDetailsUnconnected);

export const BusinessDetailsTest = BusinessDetailsUnconnected;
